import { useQuery } from '@tanstack/react-query';
import getSharedStudy from '@core/api/getSharedStudy';
import getStudyInfo from '../../features/study/api/getStudyInfo';

export const STUDY_QUERY_KEY = 'study';

const useStudyInfo = ({
  studyInstanceUidOrHash,
  isSharedStudy = false,
  retry = 3, // default react query
  useErrorBoundary,
}: {
  studyInstanceUidOrHash?: string;
  isSharedStudy?: boolean;
  retry?: number | boolean;
  useErrorBoundary?: boolean;
}) => {
  const { data: studyInfo, ...rest } = useQuery({
    queryKey: [STUDY_QUERY_KEY, studyInstanceUidOrHash],
    queryFn: () =>
      isSharedStudy
        ? getSharedStudy(studyInstanceUidOrHash!)
        : getStudyInfo(studyInstanceUidOrHash),
    enabled: !!studyInstanceUidOrHash,
    staleTime: 1000 * 60 * 10,
    retry,
    // Shared/anonymous study links (ReadOnlyViewer) render nothing while
    // studyInfo is undefined and never surface a loading/error state of
    // their own — without this, an exhausted-retry failure here (backend
    // hiccup, bad hash, etc.) left the page permanently blank with no
    // feedback. Throwing lets the route's errorElement (ErrorPage) catch
    // it instead. Scoped to isSharedStudy so every other caller of this
    // hook (the authenticated Study viewer, reports, etc.) is unaffected.
    useErrorBoundary: useErrorBoundary ?? isSharedStudy,
  });

  return { studyInfo, ...rest };
};

export default useStudyInfo;
